import * as React from 'react'
import {
  Menubar,
  MenubarMenu,
  MenubarTrigger,
  MenubarContent,
  MenubarItem,
  MenubarSeparator,
  MenubarLabel,
  MenubarCheckboxItem,
  MenubarRadioGroup,
  MenubarRadioItem,
  MenubarSub,
  MenubarSubTrigger,
  MenubarSubContent,
  MenubarGroup,
  MenubarShortcut,
} from '@/components/ui/menubar'
import { cn } from '@/lib/utils'

/**
 * Figma (node 430:17316 — Menubar):
 *
 * Menubar:     h-9, flex items-center, rounded-md, border, bg-background, p-1, shadow-sm
 * Trigger:     px-3 py-1, text-sm font-medium, rounded-sm
 *              focus:bg-accent, data-[state=open]:bg-accent
 * Content:     min-w-[12rem], rounded-md, border, bg-popover, p-1, shadow-md
 * Item:        rounded-sm, px-2 py-1.5, gap-2, text-sm
 *              Variants: Default, Checkbox, Radio, Destructive
 *              States: Default, Hover (bg-accent), Disabled (opacity-50)
 * Separator:   h-px bg-muted, -mx-1 my-1
 * SubTrigger:  same as Item + ChevronRight, data-[state=open]:bg-accent
 * Shortcut:    ml-auto text-xs text-muted-foreground
 */

/* ─── Root ─── */
const IntuiMenubar = React.forwardRef<
  React.ElementRef<typeof Menubar>,
  React.ComponentPropsWithoutRef<typeof Menubar>
>(({ className, ...props }, ref) => (
  <Menubar ref={ref} className={cn('rounded-lg', className)} {...props} />
))
IntuiMenubar.displayName = 'IntuiMenubar'

/* ─── Menu ─── */
const IntuiMenubarMenu = MenubarMenu

/* ─── Trigger ─── */
const IntuiMenubarTrigger = React.forwardRef<
  React.ElementRef<typeof MenubarTrigger>,
  React.ComponentPropsWithoutRef<typeof MenubarTrigger>
>(({ className, ...props }, ref) => (
  <MenubarTrigger ref={ref} className={cn('rounded-md', className)} {...props} />
))
IntuiMenubarTrigger.displayName = 'IntuiMenubarTrigger'

/* ─── Content ─── */
const IntuiMenubarContent = React.forwardRef<
  React.ElementRef<typeof MenubarContent>,
  React.ComponentPropsWithoutRef<typeof MenubarContent>
>(({ className, ...props }, ref) => (
  <MenubarContent ref={ref} className={cn('rounded-lg', className)} {...props} />
))
IntuiMenubarContent.displayName = 'IntuiMenubarContent'

/* ─── Item ─── */
export interface IntuiMenubarItemProps
  extends React.ComponentPropsWithoutRef<typeof MenubarItem> {
  variant?: 'default' | 'destructive'
  shortcut?: string
}

const IntuiMenubarItem = React.forwardRef<
  React.ElementRef<typeof MenubarItem>,
  IntuiMenubarItemProps
>(({ className, variant = 'default', shortcut, children, ...props }, ref) => (
  <MenubarItem
    ref={ref}
    className={cn(
      'rounded-md px-2 py-1.5 gap-2 text-sm',
      variant === 'destructive' &&
        'text-destructive focus:text-destructive focus:bg-destructive/10',
      className,
    )}
    {...(shortcut ? { 'aria-keyshortcuts': shortcut } : {})}
    {...props}
  >
    {children}
    {shortcut && <MenubarShortcut>{shortcut}</MenubarShortcut>}
  </MenubarItem>
))
IntuiMenubarItem.displayName = 'IntuiMenubarItem'

/* ─── Label ─── */
const IntuiMenubarLabel = React.forwardRef<
  React.ElementRef<typeof MenubarLabel>,
  React.ComponentPropsWithoutRef<typeof MenubarLabel>
>(({ className, ...props }, ref) => (
  <MenubarLabel
    ref={ref}
    className={cn('px-2 py-1.5 text-xs font-medium text-muted-foreground', className)}
    {...props}
  />
))
IntuiMenubarLabel.displayName = 'IntuiMenubarLabel'

/* ─── Separator ─── */
const IntuiMenubarSeparator = React.forwardRef<
  React.ElementRef<typeof MenubarSeparator>,
  React.ComponentPropsWithoutRef<typeof MenubarSeparator>
>(({ className, ...props }, ref) => (
  <MenubarSeparator ref={ref} className={cn('bg-border', className)} {...props} />
))
IntuiMenubarSeparator.displayName = 'IntuiMenubarSeparator'

/* ─── Sub ─── */
const IntuiMenubarSub = MenubarSub

/* ─── SubTrigger ─── */
const IntuiMenubarSubTrigger = React.forwardRef<
  React.ElementRef<typeof MenubarSubTrigger>,
  React.ComponentPropsWithoutRef<typeof MenubarSubTrigger>
>(({ className, ...props }, ref) => (
  <MenubarSubTrigger
    ref={ref}
    className={cn('rounded-md px-2 py-1.5 gap-2 text-sm', className)}
    {...props}
  />
))
IntuiMenubarSubTrigger.displayName = 'IntuiMenubarSubTrigger'

/* ─── SubContent ─── */
const IntuiMenubarSubContent = React.forwardRef<
  React.ElementRef<typeof MenubarSubContent>,
  React.ComponentPropsWithoutRef<typeof MenubarSubContent>
>(({ className, ...props }, ref) => (
  <MenubarSubContent ref={ref} className={cn('rounded-lg', className)} {...props} />
))
IntuiMenubarSubContent.displayName = 'IntuiMenubarSubContent'

/* ─── CheckboxItem ─── */
const IntuiMenubarCheckboxItem = React.forwardRef<
  React.ElementRef<typeof MenubarCheckboxItem>,
  React.ComponentPropsWithoutRef<typeof MenubarCheckboxItem>
>(({ className, ...props }, ref) => (
  <MenubarCheckboxItem ref={ref} className={cn('rounded-md', className)} {...props} />
))
IntuiMenubarCheckboxItem.displayName = 'IntuiMenubarCheckboxItem'

/* ─── RadioGroup ─── */
const IntuiMenubarRadioGroup = MenubarRadioGroup

/* ─── RadioItem ─── */
const IntuiMenubarRadioItem = React.forwardRef<
  React.ElementRef<typeof MenubarRadioItem>,
  React.ComponentPropsWithoutRef<typeof MenubarRadioItem>
>(({ className, ...props }, ref) => (
  <MenubarRadioItem ref={ref} className={cn('rounded-md', className)} {...props} />
))
IntuiMenubarRadioItem.displayName = 'IntuiMenubarRadioItem'

/* ─── Group ─── */
const IntuiMenubarGroup = MenubarGroup

/* ─── Shortcut ─── */
const IntuiMenubarShortcut = MenubarShortcut

export {
  IntuiMenubar,
  IntuiMenubarMenu,
  IntuiMenubarTrigger,
  IntuiMenubarContent,
  IntuiMenubarItem,
  IntuiMenubarLabel,
  IntuiMenubarSeparator,
  IntuiMenubarSub,
  IntuiMenubarSubTrigger,
  IntuiMenubarSubContent,
  IntuiMenubarCheckboxItem,
  IntuiMenubarRadioGroup,
  IntuiMenubarRadioItem,
  IntuiMenubarGroup,
  IntuiMenubarShortcut,
}
